import React, { useEffect, useRef, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './Billing.styles';
import Content from 'studio/components/Content/Content';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { formatPrice } from 'studio/helpers';
import { useMessage } from 'studio/components/Message';
import services from 'services';
import { trackEvent } from 'utils/eventTracking';
import PaymentIcon from '@mui/icons-material/PaymentRounded';
import PageTitle from 'studio/components/PageTitle';
import Link from 'studio/components/Link';

// renders the total price of an invoice
export const renderPrice = invoice => (
  <>
    <b>{formatPrice(invoice.data.total + invoice.data.tax, invoice.data.currency)}</b>
    {invoice.data.tax > 0 && ' (including taxes)'}
  </>
);

const Billing = ({ classes, user }) => {
  const { errorMessage } = useMessage();
  // reference to the content to be able to scroll it
  const contentRef = useRef(null);

  // the subscription plans
  const [plans, setPlans] = useState(null);
  // the subscription
  const [subscription, setSubscription] = useState(null);
  // the subscription's invoices
  const [invoices, setInvoices] = useState(null);
  // if true, then the loading indicator is shown
  const [loading, setLoading] = useState(false);
  // specifies if the alert with info about a scheduled update is expanded or not

  const getPlans = async () => {
    const result = await services.getPlans();
    if (result.success) {
      setPlans(result.data.data);
      return;
    }
    const message = result?.response?.data?.message || 'There was an error while fetching plans.';
    errorMessage(message);
  };

  const getSubscription = async id => {
    if (id) {
      // get a specific subscription
      const response = await services.getSubscription(id);
      if (response.success) {
        setSubscription(response.data);
      }
    } else {
      // get all subscriptions and use the first one
      const response = await services.getSubscriptions();
      if (response.success) {
        const subscriptions = response.data;
        setSubscription(subscriptions.data[0]);
      }
    }
  };

  const getInvoices = async subscriptionId => {
    const response = await services.getInvoices(subscriptionId);
    if (response.success) {
      setInvoices(response.data.data);
    }
  };

  // retrieves the initial data
  useEffect(() => {
    getPlans();
    getSubscription();
  }, []);

  // retrieves the subscription's invoices whenever it is updated
  useEffect(() => {
    if (subscription) {
      getInvoices(subscription.data.id);
    }
  }, [subscription]);

  // resets the loading flag that gets set after a subscription has been updated
  useEffect(() => {
    setLoading(false);
  }, [subscription]);

  useEffect(() => {
    trackEvent('Admin Billing Opened');
  }, []);

  return (
    <Content loading={loading || !plans || !subscription || !invoices} ref={contentRef}>
      <div className={classes.root}>
        <PageTitle icon={PaymentIcon}>Billing</PageTitle>
        <Typography variant="h2" gutterBottom>
          Subscription
        </Typography>
        <p>
          To update your subscription contact us by <Link href="mailto:support@veezoo.com">mail</Link> or{' '}
          <Link href="https://www.veezoo.com/contact" newWindow>
            book a call
          </Link>
          .
        </p>
        <p>
          Please visit the{' '}
          <Link href="https://www.veezoo.com/pricing" newWindow>
            pricing page
          </Link>{' '}
          to see the available plans.
        </p>
        <Button color="primary" variant="contained" href="mailto:support@veezoo.com">
          Contact us
        </Button>
      </div>
    </Content>
  );
};

export default withStyles(styles)(Billing);
